import { HttpService } from '../client';
import {
  ActionStatus,
  ClicktermInlineFinalizeResult,
  ClicktermInlineHandle,
  ClickwrapTemplateContent,
  InteractionType,
} from '../models';
import { InlineRef } from '../ui/renderer';
import { ClickwrapError } from '../utils';

export interface InlineClickwrapHandleOptions {
  service: HttpService;
  view: InlineRef;
  content: ClickwrapTemplateContent;
  endUserId: string;
  interactionType?: InteractionType;
  onChange?: (checked: boolean) => void;
  onDestroy?: () => void;
}

export class InlineClickwrapHandle implements ClicktermInlineHandle {
  private checked: boolean;
  private destroyed = false;
  private finalizePromise: Promise<ClicktermInlineFinalizeResult> | null = null;
  private currentResult: ClicktermInlineFinalizeResult | null = null;

  constructor(private readonly options: InlineClickwrapHandleOptions) {
    this.checked = options.view.checkbox.checked;
    this.options.view.checkbox.addEventListener('change', this.handleChange);
  }

  isChecked(): boolean {
    this.checked = this.options.view.checkbox.checked;
    return this.checked;
  }

  getCurrentResult(): ClicktermInlineFinalizeResult | null {
    return this.currentResult;
  }

  finalize(): Promise<ClicktermInlineFinalizeResult> {
    this.lock();

    if (this.finalizePromise) {
      return this.finalizePromise;
    }

    const clickwrapEventStatus: ActionStatus = this.isChecked() ? 'ACCEPTED' : 'DECLINED';
    this.finalizePromise = this.options.service.applyAgreement({
      clickwrapTemplateId: this.options.content.clickwrapTemplateId,
      clickwrapTemplateVersion: this.options.content.clickwrapTemplateVersion,
      clickwrapTemplateVersionMinor: this.options.content.clickwrapTemplateVersionMinor,
      endUserId: this.options.endUserId,
      clickwrapEventStatus,
      interactionType: this.options.interactionType,
    }).then((signature) => {
      const result = {
        status: 'UNVERIFIED' as const,
        clicktermSignature: signature.clicktermSignature,
        submittedStatus: clickwrapEventStatus,
      };
      this.currentResult = result;
      return result;
    }).catch((error) => {
      const result = {
        status: 'ERROR' as const,
        clicktermSignature: null,
        submittedStatus: clickwrapEventStatus,
        error: normalizeInlineFinalizeError(error),
      };
      this.currentResult = result;
      this.finalizePromise = null;
      this.options.view.setLocked(false);
      return result;
    });

    return this.finalizePromise;
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.options.view.checkbox.removeEventListener('change', this.handleChange);
    this.options.view.root.remove();
    this.options.onDestroy?.();
  }

  private lock(): void {
    this.options.view.setLocked(true);
  }

  private readonly handleChange = (): void => {
    this.checked = this.options.view.checkbox.checked;
    try {
      this.options.onChange?.(this.checked);
    } catch (error) {
      console.error('Clickterm inline onChange callback failed.', error);
    }
  };
}

export class AlreadyAcceptedInlineHandle implements ClicktermInlineHandle {
  private readonly currentResult: ClicktermInlineFinalizeResult = {
    status: 'ALREADY_ACCEPTED',
    clicktermSignature: null,
  };
  private readonly finalizePromise = Promise.resolve<ClicktermInlineFinalizeResult>(this.currentResult);
  private destroyed = false;

  constructor(private readonly onDestroy?: () => void) {}

  isChecked(): boolean {
    return true;
  }

  getCurrentResult(): ClicktermInlineFinalizeResult {
    return this.currentResult;
  }

  finalize(): Promise<ClicktermInlineFinalizeResult> {
    return this.finalizePromise;
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.onDestroy?.();
  }
}

export class ExistingSignatureInlineHandle implements ClicktermInlineHandle {
  private readonly currentResult: ClicktermInlineFinalizeResult;
  private readonly finalizePromise: Promise<ClicktermInlineFinalizeResult>;
  private destroyed = false;

  constructor(clicktermSignature: string, private readonly onDestroy?: () => void) {
    this.currentResult = {
      status: 'UNVERIFIED',
      clicktermSignature,
    };
    this.finalizePromise = Promise.resolve(this.currentResult);
  }

  isChecked(): boolean {
    return false;
  }

  getCurrentResult(): ClicktermInlineFinalizeResult {
    return this.currentResult;
  }

  finalize(): Promise<ClicktermInlineFinalizeResult> {
    return this.finalizePromise;
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.onDestroy?.();
  }
}

const normalizeInlineFinalizeError = (error: unknown): Error => {
  if (error instanceof ClickwrapError) {
    return error;
  }
  const reason = error instanceof Error ? error.message : 'Unknown error';
  return new ClickwrapError(`Failed to send event: ${reason}`, error);
};
